export const AD_LIFETIME_DAYS = 60;

export type AdStatRow = {
  slug: string;
  title: string;
  price: number;
  category: string;
  created_at: string;
  expires_at: string;
  is_paused: boolean;
  promoted_until: string | null;
  impressions: number;
  views: number;
  uniqueViews: number;
  viewsLast7: number;
  contacts: number;
  shares: number;
};

export type DailyStatPoint = {
  /** ISO-datum, t.ex. 2026-08-06. */
  date: string;
  views: number;
  uniqueViews: number;
  contacts: number;
  shares: number;
};

export type AdInsight = {
  tone: "good" | "warn" | "info";
  title: string;
  body: string;
  /** Föreslaget nytt pris i kronor, om rekommendationen handlar om pris. */
  suggestedPrice?: number;
};

export function daysLeft(expiresAt: string): number {
  return Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86_400_000);
}

export function daysLive(createdAt: string): number {
  return Math.max(1, Math.floor((Date.now() - new Date(createdAt).getTime()) / 86_400_000));
}

function roundPrice(value: number): number {
  if (value >= 5000) return Math.round(value / 500) * 500;
  if (value >= 1000) return Math.round(value / 100) * 100;
  if (value >= 200) return Math.round(value / 50) * 50;
  return Math.max(10, Math.round(value / 10) * 10);
}

/** Enkla, konkreta råd baserat på hur annonsen presterar. */
export function buildAdInsights(row: AdStatRow): AdInsight[] {
  const out: AdInsight[] = [];
  const left = daysLeft(row.expires_at);
  const live = daysLive(row.created_at);
  const viewsPerDay = row.views / live;

  if (row.is_paused && left <= 0) {
    out.push({
      tone: "warn",
      title: "Annonsen har gått ut",
      body: `Annonser ligger uppe i ${AD_LIFETIME_DAYS} dagar. Förnya den så syns den igen direkt.`,
    });
  } else if (row.is_paused) {
    out.push({
      tone: "info",
      title: "Pausad annons",
      body: "Ingen kan hitta den just nu. Aktivera den igen när du vill sälja.",
    });
  } else if (left <= 7) {
    out.push({
      tone: "warn",
      title: `Går ut om ${Math.max(left, 0)} dagar`,
      body: "Förnya annonsen så pausas den inte automatiskt.",
    });
  }

  if (row.views >= 25 && row.contacts === 0) {
    const cut = row.views >= 80 ? 0.85 : 0.9;
    out.push({
      tone: "warn",
      title: "Många tittar men ingen hör av sig",
      body: `${row.views} visningar utan ett enda meddelande brukar betyda att priset ligger för högt. Testa att sänka till ungefär ${roundPrice(row.price * cut).toLocaleString("sv-SE")} kr.`,
      suggestedPrice: roundPrice(row.price * cut),
    });
  }

  if (!row.is_paused && live >= 5 && viewsPerDay < 1) {
    out.push({
      tone: "info",
      title: "Få visningar",
      body: "Byt till en ljusare förstabild, gör rubriken tydligare och lägg till fler detaljer i beskrivningen. Ett lyft placerar annonsen högst upp i listan.",
    });
  }

  if (!row.is_paused && live >= 21 && row.contacts === 0 && row.views < 25) {
    out.push({
      tone: "warn",
      title: `Ingen aktivitet på ${live} dagar`,
      body: `Sänk priset till ungefär ${roundPrice(row.price * 0.85).toLocaleString("sv-SE")} kr eller skriv om annonsen – nya annonser får mest uppmärksamhet.`,
      suggestedPrice: roundPrice(row.price * 0.85),
    });
  }

  if (row.contacts > 0 && row.contacts >= Math.max(2, row.views * 0.1)) {
    out.push({
      tone: "good",
      title: "Annonsen går bra",
      body: `${row.contacts} personer har hört av sig. Svara snabbt – de flesta affärer görs inom ett dygn.`,
    });
  }

  if (!out.length) {
    out.push({
      tone: "good",
      title: "Allt ser bra ut",
      body: `Annonsen är aktiv i ${Math.max(left, 0)} dagar till. Vi hör av oss om något behöver justeras.`,
    });
  }

  return out;
}
